import { z } from "zod";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { loyaltyEntryStatusSchema, loyaltyEntryTypeSchema } from "./enums.js";
import { loyaltyPointsSchema, loyaltySignedPointsSchema } from "./program.js";
/**
 * Puan hesabı ve defteri (ADR 0017 §19–22). Bakiye = işlenmiş (`POSTED`) hareketlerin toplamı; iade yüzünden eksi
 * olabilir. Kazanım partileri FIFO tüketilir. Hesap satırı müşterinin ilk hareketinde açılır; hareketi olmayan müşteri
 * için uç sıfır bakiye döner (`hasAccount: false`).
 */
export const loyaltyAccountParamsSchema = z.object({ customerProfileId: uuidSchema });
export const loyaltyEntryParamsSchema = z.object({ entryId: uuidSchema });
export const loyaltyAccountSchema = z.object({
    customerProfileId: uuidSchema,
    customerCode: z.string(),
    loyaltyCardNo: z.string().nullable().describe("Takma ad niteliğinde (kişisel veri sayılmaz)"),
    hasAccount: z.boolean(),
    pointsBalance: loyaltySignedPointsSchema,
    balanceValue: moneySchema.describe("Bakiyenin güncel puan değeriyle ₺ karşılığı (kuruş)"),
    pendingPoints: loyaltyPointsSchema.describe("İncelemede bekleyen kazanım (bakiyeye girmez)"),
    lifetimeEarned: loyaltyPointsSchema.describe("İşlenmiş kazanımların toplamı (geri almalar düşülmez)"),
    pointValue: z.string(),
    tier: z
        .object({
        code: z.string(),
        name: z.string(),
        rank: z.number().int(),
        earnMultiplier: z.string(),
        thresholdPoints: loyaltyPointsSchema.describe("Mevcut seviyenin eşiği (pencere puanı); ilerleme çubuğu bu eşikten `nextTier.thresholdPoints`'e gider"),
    })
        .nullable()
        .describe("Seviye yoksa null (ilerleme 0'dan başlar)"),
    windowPoints: loyaltySignedPointsSchema.describe("Seviye penceresinde kazanılan − geri alınan puan"),
    nextTier: z
        .object({
        code: z.string(),
        name: z.string(),
        thresholdPoints: loyaltyPointsSchema,
        pointsNeeded: loyaltyPointsSchema,
    })
        .nullable(),
    tierEvaluatedAt: timestampSchema.nullable(),
    tierChangedAt: timestampSchema.nullable(),
    redeemBlocked: z.boolean(),
    redeemBlockReason: z.string().nullable(),
    lots: z.object({
        openCount: z.number().int(),
        openPoints: loyaltyPointsSchema,
    }),
    nextExpiry: z.object({ expiresAt: timestampSchema, points: loyaltyPointsSchema }).nullable(),
    openedAt: timestampSchema.nullable(),
    updatedAt: timestampSchema.nullable(),
    version: z.number().int(),
});
export const loyaltyEntrySchema = z.object({
    id: uuidSchema,
    customerProfileId: uuidSchema,
    entryType: loyaltyEntryTypeSchema,
    status: loyaltyEntryStatusSchema,
    points: loyaltySignedPointsSchema,
    value: moneySchema.describe("₺ karşılığı (kuruş), puanla aynı işaret"),
    occurredAt: timestampSchema,
    businessDate: localDateSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    sourceModule: z.string(),
    sourceDocType: z.string(),
    sourceDocId: uuidSchema,
    sourceLineRef: z.string(),
    details: z.record(z.string(), z.unknown()).nullable().describe("Kazanım satır kırılımı / parti dağılımı"),
    reason: z.string().nullable(),
    journalClosingId: uuidSchema.nullable().describe("Sadakat gün kapanışı (EARN / REVERSAL / EXPIRE)"),
    journalEntryId: uuidSchema.nullable().describe("Anında kesilen fiş (ADJUST)"),
    reviewedBy: uuidSchema.nullable(),
    reviewedAt: timestampSchema.nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
});
export const loyaltyEntryPageSchema = pageOf(loyaltyEntrySchema);
export const listLoyaltyEntriesQuerySchema = pageQuerySchema.extend({
    entryType: loyaltyEntryTypeSchema.optional(),
    status: loyaltyEntryStatusSchema.optional(),
    from: localDateSchema.optional().describe("İş günü ≥"),
    to: localDateSchema.optional().describe("İş günü ≤"),
});
export const createLoyaltyAdjustmentSchema = z.object({
    points: loyaltySignedPointsSchema.refine((value) => BigInt(value) !== 0n, "Düzeltme sıfır olamaz"),
    reason: z.string().trim().min(3).max(300),
    stationId: uuidSchema
        .nullable()
        .default(null)
        .describe("Muhasebe boyutu ve parti istasyonu; boşsa müşterinin ana istasyonu"),
});
export const loyaltyLedgerStatusSchema = z.enum(["POSTED", "QUEUED", "NOT_REQUIRED"]);
export const loyaltyAdjustmentResultSchema = z.object({
    entry: loyaltyEntrySchema,
    account: loyaltyAccountSchema,
    ledgerStatus: loyaltyLedgerStatusSchema,
});
export const setLoyaltyRedemptionBlockSchema = z
    .object({
    blocked: z.boolean(),
    reason: z.string().trim().min(3).max(300).nullable().default(null),
})
    .refine((value) => !value.blocked || value.reason !== null, {
    path: ["reason"],
    message: "Harcamayı kapatmak için gerekçe zorunludur",
});
// ── Kötüye kullanım incelemesi ───────────────────────────────────────────────
export const loyaltyReviewSchema = loyaltyEntrySchema.extend({
    customerCode: z.string(),
    customerMaskedName: z.string().describe("DAİMA maskeli ad (`A*** Y***`; adsız / anonim profilde `—`)"),
    customerName: z
        .string()
        .nullable()
        .describe("`customers.personal.view` varsa açık ad, yoksa maskeli ad; profilde ad yoksa null"),
    personalDataMasked: z.boolean().describe("`customerName` maskeli mi (`customers.personal.view` yok)"),
    verdict: z.string().nullable().describe("TOO_FREQUENT | DAILY_CAP"),
});
export const loyaltyReviewPageSchema = pageOf(loyaltyReviewSchema);
export const listLoyaltyReviewsQuerySchema = pageQuerySchema.extend({
    customerProfileId: uuidSchema.optional(),
});
export const rejectLoyaltyReviewSchema = z.object({ reason: z.string().trim().min(3).max(300) });
//# sourceMappingURL=accounts.js.map